import React, { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { CREATE_LOADS, ASSIGN_DRIVER_TO_LOAD_AND_SAVE_LOAD } from '../../graphql/client/mutations';
import ConfirmLoadsModal from './ConfirmLoadsModal';
import InitialModal from './InitialModal';
import LoadListModal from './LoadListModal';
import AddDriverModal from './AddDriverModal';
import DispatchModal from './DispatchModal';
import { useStoreContext } from '../App/StoreProvider';
import { useAuthContext } from '../Auth/AuthProvider';
import { PermissionEnum } from '../../types/PermissionEnum';
import * as Actions from '../../store/actions/addLoadModal';
import './AddLoads.scss';
import { GET_LOAD_SUMMARY } from '../../graphql/client/queries';
import {
    AssignDriverToLoadAndSaveLoad,
    AssignDriverAndSaveLoadListEdit,
    AssignDriverAndSaveLoadInput,
    CreateLoads,
} from '../../types/schemaTypes';

const modalList = [InitialModal, ConfirmLoadsModal, LoadListModal, AddDriverModal, DispatchModal];
interface AddLoadsModalProps {
    proppantName?: string;
    sandPo?: string;
    transportationPo?: string;
    wellhead?: string;
    lofa?: string;
    lofaId: number;
    jobId: number;
    jobSummaryId?: number;
    customerId: number;
    region?: {
        regionName: string | null;
        id: number | null;
        extSystemName: string | null;
        extSystemReference: string | null;
        regionCode: string | null;
    } | null;
    loadsRemaining?: number;
    handleShowModal?: () => void;
}

const AddLoadsModal = ({
    proppantName,
    sandPo,
    transportationPo,
    wellhead,
    lofa,
    lofaId,
    jobId,
    jobSummaryId,
    customerId,
    region,
    loadsRemaining,
    handleShowModal,
}: AddLoadsModalProps): React.ReactElement => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showModal, setShowModal] = useState(false);
    const [showDispatchModal, setShowDispatchModal] = useState(false);
    const [state, dispatch] = useStoreContext();
    const ModalComponent = modalList[currentIndex];
    const {
        addLoadModal: { numberOfLoadsToCreate, loadIds, currentDriverIds },
    } = state;
    const isAddLoadEnabled = useAuthContext().userHasPermission(PermissionEnum.AddLoads);
    const variables = {
        input: {
            jobSummaryId: jobSummaryId,
            numberToCreate: numberOfLoadsToCreate,
            transportationPo: transportationPo,
        },
    };
    const [addLoad, { loading: mutationLoading }] = useMutation<CreateLoads>(CREATE_LOADS, {
        onCompleted({ createLoads }) {
            dispatch(Actions.setCreatedLoadIds((createLoads?.loadIds ?? []).filter((id): id is number => id != null)));
        },
    });
    // const [deleteLoad] = useMutation(DELETE_LOADS, {
    //     onCompleted() {
    //         dispatch(Actions.setCreatedLoadIds([]));
    //     },
    //     refetchQueries: () => {
    //         return [
    //             {
    //                 query: GET_LOAD_SUMMARY,
    //                 variables: { jobId: jobId },
    //             },
    //         ];
    //     },
    // });

    const handleClickNext = async (): Promise<void> => {
        if (currentIndex < modalList.length - 1) {
            const newIndex = currentIndex + 1;
            setCurrentIndex(newIndex);
            if (newIndex === 2) {
                await addLoad({ variables });
            }
        } else {
            setCurrentIndex(0);
            setShowModal(false);

            dispatch(Actions.setNumberOfLoadsToCreate(0));
            dispatch(Actions.setTmsIds([]));
        }
    };

    const handleClickBack = async (isSplit?: boolean): Promise<void> => {
        if (currentIndex === 2) {
            // const variables = { input: { loadIds } };
            // await deleteLoad({ variables });
            setCurrentIndex(0);
        } else if (isSplit === true) {
            // If split load, go directly from AddDriverModal -> DispatchModal
            setCurrentIndex(4);
        } else if (currentIndex !== 0) {
            setCurrentIndex(currentIndex - 1);
        } else {
            setCurrentIndex(0);
            setShowModal(false);

            dispatch(Actions.setNumberOfLoadsToCreate(0));
            dispatch(Actions.setTmsIds([]));
        }
    };

    const onAssignDriverAndSaveLoad = (data: AssignDriverToLoadAndSaveLoad): void => {
        if (data.assignDriverToLoadAndSaveLoad === null) return;
        const assignDriverToLoadAndSaveLoadErrors = data.assignDriverToLoadAndSaveLoad.errors;
        const assignDriverToLoadAndSaveLoadLoadIds = data.assignDriverToLoadAndSaveLoad.loadIds;
        let i = 0;
        if (
            assignDriverToLoadAndSaveLoadErrors &&
            assignDriverToLoadAndSaveLoadErrors.length > 0 &&
            assignDriverToLoadAndSaveLoadLoadIds &&
            assignDriverToLoadAndSaveLoadLoadIds.length > 0
        ) {
            assignDriverToLoadAndSaveLoadLoadIds.forEach(it => {
                dispatch(Actions.addFailedLoadId(it || 0));
                dispatch(Actions.removeDriverFromLoad(it || 0));
                dispatch(Actions.setErrorMessageForLoad(it || 0, assignDriverToLoadAndSaveLoadErrors[i] || ''));
                i++;
            });
            dispatch(Actions.removeSuccessfulLoadsFromList());
            setCurrentIndex(2);
        } else {
            setCurrentIndex(0);
            setShowModal(false);

            dispatch(Actions.setNumberOfLoadsToCreate(0));
            dispatch(Actions.setDriverIds([]));
            dispatch(Actions.setFailedLoadIds([]));
            dispatch(Actions.setTmsIds([]));
        }
        setShowDispatchModal(true);
    };
    const callModal = (err: any): any => {
        if (err.message && err.message === 'Network error: Failed to fetch') {
            setCurrentIndex(0);
            setShowModal(false);

            dispatch(Actions.setNumberOfLoadsToCreate(0));
            dispatch(Actions.setDriverIds([]));
            dispatch(Actions.setFailedLoadIds([]));
            dispatch(Actions.setTmsIds([]));
            if (handleShowModal) {
                handleShowModal();
            }
        }
    };

    const [assignDriverAndSaveLoad, { loading: assignDriverMutationLoading }] = useMutation<
        AssignDriverToLoadAndSaveLoad
    >(ASSIGN_DRIVER_TO_LOAD_AND_SAVE_LOAD, {
        onCompleted: onAssignDriverAndSaveLoad,
        onError: er => {
            callModal(er);
        },
        refetchQueries: () => {
            return [
                {
                    query: GET_LOAD_SUMMARY,
                    variables: { jobId: jobId },
                },
            ];
        },
    });

    const handleOnSave = async (): Promise<void> => {
        if (currentIndex === 2) {
            const assignDriverAndSaveLoadInputList: AssignDriverAndSaveLoadInput[] = [];
            state.addLoadModal.loadsWithDrivers &&
                state.addLoadModal.loadsWithDrivers.forEach(async it => {
                    const tmsIds = state.addLoadModal.tmsIds;
                    if (tmsIds.length > 0) {
                        const newAssignDriverAndSaveLoadInput: AssignDriverAndSaveLoadInput = {
                            loadId: it.id,
                            driverId: it.driverId,
                            tmsId: it.tmsId.toString(),
                        };
                        assignDriverAndSaveLoadInputList.push(newAssignDriverAndSaveLoadInput);
                    } else {
                        const newAssignDriverAndSaveLoadInput: AssignDriverAndSaveLoadInput = {
                            loadId: it.id,
                            driverId: it.driverId,
                            tmsId: '',
                        };
                        assignDriverAndSaveLoadInputList.push(newAssignDriverAndSaveLoadInput);
                    }
                });
            const newAssignDriverAndSaveLoadListEdit: AssignDriverAndSaveLoadListEdit = {
                assignDriverAndSaveLoad: assignDriverAndSaveLoadInputList,
            };
            await assignDriverAndSaveLoad({
                variables: { input: newAssignDriverAndSaveLoadListEdit },
            });
        }
    };

    const handleOnClickHide = async (): Promise<void> => {
        // if (currentIndex === 2 || currentIndex === 3) {
        //     const variables = { input: { loadIds } };
        //     await deleteLoad({ variables });
        // }
        setCurrentIndex(0);
        setShowModal(false);

        dispatch(Actions.setNumberOfLoadsToCreate(0));
        dispatch(Actions.setTmsIds([]));
        dispatch(Actions.setDriverIds([]));
        dispatch(Actions.setFailedLoadIds([]));
    };

    const handleHideWhenRemoveAllCreatedLoads = (): void => {
        setCurrentIndex(0);
        setShowModal(false);

        dispatch(Actions.setNumberOfLoadsToCreate(0));
        dispatch(Actions.setTmsIds([]));
        dispatch(Actions.setDriverIds([]));
        dispatch(Actions.setFailedLoadIds([]));
    };

    return (
        <React.Fragment>
            {isAddLoadEnabled && (
                <div className="Add-Load-Container Row" onClick={() => setShowModal(true)}>
                    <i className="material-icons Add-Load-Icon">add_circle</i>
                    <span className="Add-Load-Link">Add Load</span>
                </div>
            )}
            <ModalComponent
                jobId={jobId}
                numberOfLoadsToCreate={numberOfLoadsToCreate}
                onClickShow={() => setShowModal(true)}
                onClickNext={handleClickNext}
                show={showModal}
                region={region}
                wellhead={wellhead}
                purchaseOrder={sandPo}
                proppantName={proppantName}
                lofa={lofa}
                lofaId={lofaId}
                onClickBack={handleClickBack}
                onClickHide={handleOnClickHide}
                onClickSave={handleOnSave}
                customerId={customerId}
                isFromLoads={false}
                isSplitLoad={false}
                loadsRemaining={loadsRemaining || 0}
                sandPO={sandPo}
                mutationLoading={mutationLoading}
                handleHideWhenRemoveAllCreatedLoads={handleHideWhenRemoveAllCreatedLoads}
                isFromAddLoads={true}
                disabled={assignDriverMutationLoading}
                driverIds={currentDriverIds}
            />
            <DispatchModal
                isFromLoads={true}
                isSplitLoad={false}
                show={showDispatchModal}
                onClickHide={() => setShowDispatchModal(false)}
            />
        </React.Fragment>
    );
};

export default AddLoadsModal;
